import { useEffect, useRef, useState } from "react";
import { getPhones, createAdminPhone, updateAdminPhone, deleteAdminPhone, uploadImage, resolveImageUrl, formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, Smartphone, Upload, ImageIcon } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const EMPTY = { brand: "", name: "", image: "", price: "", badge: "", subscription_included: "12 mesi", specs: "", description: "", order: 0 };

export const AdminPhones = ({ token }) => {
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    return getPhones()
      .then((p) => setPhones(p))
      .catch(() => toast.error("Errore nel caricamento dei telefoni"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine (JPG, PNG, WEBP...).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5 MB).");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImage(token, file);
      set("image", res.url);
      toast.success("Foto caricata correttamente.");
    } catch (err) {
      toast.error("Errore nel caricamento della foto.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (phone) => {
    setEditingId(phone.id);
    setForm({
      brand: phone.brand || "",
      name: phone.name || "",
      image: phone.image || "",
      price: phone.price ?? "",
      badge: phone.badge || "",
      subscription_included: phone.subscription_included || "12 mesi",
      specs: (phone.specs || []).join("\n"),
      description: phone.description || "",
      order: phone.order || 0,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.brand || !form.name || !form.image || form.price === "") {
      toast.error("Marca, modello, immagine e prezzo sono obbligatori.");
      return;
    }
    const payload = {
      brand: form.brand,
      name: form.name,
      image: form.image,
      price: Number(form.price),
      badge: form.badge || "",
      subscription_included: form.subscription_included || "12 mesi",
      specs: String(form.specs).split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
      description: form.description || "",
      order: Number(form.order) || 0,
    };
    setSaving(true);
    try {
      if (editingId) {
        await updateAdminPhone(token, editingId, payload);
        toast.success("Telefono aggiornato.");
      } else {
        await createAdminPhone(token, payload);
        toast.success("Telefono aggiunto.");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error("Errore nel salvataggio del telefono.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteAdminPhone(token, toDelete.id);
      toast.success("Telefono eliminato.");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error("Errore nell'eliminazione.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Gestisci il catalogo dei telefoni preconfigurati (aggiungi, modifica, elimina).</p>
        <Button onClick={openNew} data-testid={ADMIN.addPhone} className="rounded-xl glow-hover">
          <Plus className="h-4 w-4" /> Aggiungi telefono
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-72 rounded-2xl" />
      ) : (
        <Card className="rounded-2xl border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Foto</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Modello</TableHead>
                <TableHead>Prezzo</TableHead>
                <TableHead>Badge</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {phones.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    {p.image ? (
                      <img
                        src={resolveImageUrl(p.image)}
                        alt={`${p.brand} ${p.name}`}
                        data-testid={`admin-phone-thumb-${p.id}`}
                        className="h-12 w-12 rounded-lg object-cover border border-border bg-secondary"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-secondary text-muted-foreground">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{p.brand}</TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell className="font-semibold">{formatEUR(p.price)}</TableCell>
                  <TableCell>{p.badge ? <Badge variant="secondary" className="rounded-md">{p.badge}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`admin-edit-phone-${p.id}`} className="rounded-lg">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setToDelete(p)} data-testid={`${ADMIN.deletePhone}-${p.id}`} className="rounded-lg text-destructive hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {phones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Nessun telefono nel catalogo.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog Aggiungi/Modifica */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-accent" /> {editingId ? "Modifica telefono" : "Nuovo telefono"}</DialogTitle>
            <DialogDescription>Compila i dettagli del dispositivo. I campi con * sono obbligatori.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-brand">Marca *</Label>
              <Input id="f-brand" data-testid={ADMIN.phoneBrand} value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Samsung" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-name">Modello *</Label>
              <Input id="f-name" data-testid={ADMIN.phoneName} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Galaxy S24" className="rounded-xl" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Foto del telefono *</Label>
              <div className="flex items-start gap-4">
                {form.image ? (
                  <img
                    src={resolveImageUrl(form.image)}
                    alt="Anteprima"
                    data-testid="admin-phone-image-preview"
                    className="h-24 w-24 shrink-0 rounded-xl object-cover border border-border bg-secondary"
                  />
                ) : (
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl border border-dashed border-border bg-secondary text-muted-foreground">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    data-testid="admin-phone-file-input"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="admin-phone-upload-button"
                    className="rounded-xl w-full"
                  >
                    <Upload className="h-4 w-4" /> {uploading ? "Caricamento..." : "Carica foto dal PC"}
                  </Button>
                  <p className="text-xs text-muted-foreground">JPG, PNG o WEBP · max 5 MB. In alternativa incolla un URL qui sotto.</p>
                  <Input
                    id="f-image"
                    data-testid={ADMIN.phoneImage}
                    value={form.image}
                    onChange={(e) => set("image", e.target.value)}
                    placeholder="https://... oppure /api/images/..."
                    className="rounded-xl text-xs"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-price">Prezzo (EUR) *</Label>
              <Input id="f-price" type="number" min="0" data-testid={ADMIN.phonePrice} value={form.price} onChange={(e) => set("price", e.target.value)} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-badge">Badge</Label>
              <Input id="f-badge" value={form.badge} onChange={(e) => set("badge", e.target.value)} placeholder="Top di gamma" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-sub">Abbonamento incluso</Label>
              <Input id="f-sub" value={form.subscription_included} onChange={(e) => set("subscription_included", e.target.value)} placeholder="12 mesi" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-order">Ordine</Label>
              <Input id="f-order" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="rounded-xl" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="f-specs">Specifiche (una per riga)</Label>
              <Textarea id="f-specs" rows={4} value={form.specs} onChange={(e) => set("specs", e.target.value)} placeholder={"Display 6.8\" AMOLED\n12GB RAM\n256GB"} className="rounded-xl" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="f-desc">Descrizione</Label>
              <Textarea id="f-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Annulla</Button>
            <Button onClick={submit} disabled={saving} data-testid={ADMIN.savePhone} className="rounded-xl glow-hover">
              <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma eliminazione */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo telefono?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `\"${toDelete.brand} ${toDelete.name}\" verra' rimosso dal catalogo. L'azione non puo' essere annullata.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
